import Link from "next/link";
import { useEffect, useState } from "react";
import { useRouter } from "next/router";

import useVideoSermons from "../../hooks/use-video-sermons";
import SermonsSlider from "../../components/sermons/sermons-slider";
import VideosSlider from "../../components/videos/videos-slider";
import ChurchesSlider from "../../components/churches/churches-slider";
import usePaginatedChurches from "../../hooks/churches/use-paginated-churches";
import usePaginatedAudioSermons from "../../hooks/sermons/use-paginated-audio-sermons";
import useDebounce from "../../hooks/use-debounce";
import usePageEvents from "../../hooks/use-page-events";
import HeadMeta from "../../components/HeadMeta";

export default function Sermons() {
  const router = useRouter();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 3000);

  const sermonsQuery = usePaginatedAudioSermons();
  const churchesQuery = usePaginatedChurches();
  const videoSermonsQuery = useVideoSermons();

  const sendAnalytics = usePageEvents("sermons");

  const goBack = () => {
    router.back();
  };

  return (
    <div className="w-full">
      <HeadMeta title="Sermons" url="/sermons" />

      <div className="py-4">
        <div className="flex items-center px-3">
          <button
            onClick={goBack}
            className="flex items-center space-x-2 focus:outline-none focus:text-gray-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-8 h-8 -ml-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <div className="flex items-center pb-1 ml-5 sm:ml-6">
            <p className="flex-1 text-2xl font-bold text-gray-800 sm:text-3xl">Sermons</p>
          </div>
        </div>

        <div className="px-3 mt-5">
          <form
            onSubmit={(e: React.FormEvent) => {
              e.preventDefault();
              setSearchQuery(searchTerm);
            }}
          >
            <input
              type="text"
              value={searchTerm}
              placeholder="Search Sermons"
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                setSearchTerm(e.currentTarget.value)
              }
              className="w-full px-6 py-3 text-white search-input"
            />
          </form>
        </div>

        <div className="mt-4">
          <p className="flex items-center p-3">
            <span className="flex-1 text-lg font-bold text-gray-800">Latest Sermons</span>

            <Link href="/sermons/latest-sermons">
              <a className="text-xs font-bold">View All</a>
            </Link>
          </p>

          <SermonsSlider sermons={sermonsQuery.data} isLoading={sermonsQuery.isLoading} />
        </div>

        <div className="mt-2">
          <p className="flex items-center p-3">
            <span className="flex-1 text-lg font-bold text-gray-800">Latest Video Sermons</span>

            <Link href="/sermons/video-sermons">
              <a className="text-xs font-bold">View All</a>
            </Link>
          </p>

          <VideosSlider videos={videoSermonsQuery.data} isLoading={videoSermonsQuery.isLoading} />
        </div>

        <div className="mt-4">
          <p className="flex items-center p-3">
            <span className="flex-1 text-lg font-bold text-gray-800">Popular Churches</span>

            <Link href="/churches">
              <a className="text-xs font-bold">View All</a>
            </Link>
          </p>

          <ChurchesSlider churches={churchesQuery.data} isLoading={churchesQuery.isLoading} />
        </div>
      </div>
    </div>
  );
}
